import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate } from "../lib/api";
import { Check } from "lucide-react";
import ConfirmMarkPaidDialog from "../components/ConfirmMarkPaidDialog";
import { toast } from "sonner";

export default function CalendarPage() {
  const [summary, setSummary] = useState(null);
  const [confirmPay, setConfirmPay] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/portfolio/summary");
    setSummary(data);
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    if (!summary?.upcoming_payments) return {};
    const g = {};
    summary.upcoming_payments.forEach((p) => {
      const k = p.date.slice(0, 7);
      g[k] = g[k] || [];
      g[k].push(p);
    });
    return g;
  }, [summary]);

  const onMarkPaid = async () => {
    if (!confirmPay) return;
    setBusy(true);
    try {
      await api.post("/dividends/mark-paid", {
        holding_id: confirmPay.holding_id,
        payment_date: confirmPay.date,
        amount: confirmPay.amount,
      });
      toast.success(`Recorded ${confirmPay.ticker} payment`);
      setConfirmPay(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 sticky top-0 bg-champagne z-10">
        <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::FORECAST</div>
        <h1 className="text-2xl font-bold tracking-tight text-graphite">Dividend Calendar — 12 months</h1>
      </div>

      <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone border border-stone">
        {Object.entries(grouped).map(([month, items]) => {
          const total = items.reduce((s, p) => s + p.amount, 0);
          const dt = new Date(month + "-01");
          const label = dt.toLocaleDateString("en-US", { month: "long", year: "numeric" });
          return (
            <div key={month} className="bg-champagne p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">{month}</div>
                  <div className="text-base font-semibold text-graphite">{label}</div>
                </div>
                <div className="text-lg font-mono text-groen-dark">{fmtMoney(total)}</div>
              </div>
              <div className="space-y-px bg-stone">
                {items.map((p) => (
                  <div key={`${p.holding_id || p.ticker}-${p.date}`} className="bg-champagne py-2 px-3 flex items-center justify-between group">
                    <div>
                      <div className="text-sm font-mono text-paars-dark">{p.ticker}</div>
                      <div className="text-[10px] font-mono text-graphite/45">{fmtDate(p.date)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-mono text-graphite">{fmtMoney(p.amount)}</div>
                      <button
                        data-testid={`cal-mark-paid-${p.holding_id}-${p.date}`}
                        onClick={() => setConfirmPay(p)}
                        title="Mark as paid"
                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-groen-dark hover:bg-stone/50 border border-groen/30 hover:border-groen"
                      >
                        <Check className="w-3 h-3" strokeWidth={2.5} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {Object.keys(grouped).length === 0 && (
          <div className="bg-champagne p-12 text-center text-graphite/45 font-mono text-sm col-span-full">
            NO UPCOMING PAYMENTS. ADD A POSITION TO BEGIN FORECASTING.
          </div>
        )}
      </div>

      <ConfirmMarkPaidDialog
        open={!!confirmPay}
        onOpenChange={(o) => !o && setConfirmPay(null)}
        payment={confirmPay}
        onConfirm={onMarkPaid}
        busy={busy}
      />
    </div>
  );
}
